import React, { useRef, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { UserGotySelectionsResponse, GotyCategory, GotyCategoryLabels } from '../../types/Goty';

interface GotyThumbnailGeneratorProps {
  data: UserGotySelectionsResponse;
  year: number;
  isMyGoty: boolean;
}

const GotyThumbnailGenerator: React.FC<GotyThumbnailGeneratorProps> = ({ data, year, isMyGoty }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const generateThumbnail = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 16:9 aspect ratio for social media
    canvas.width = 1200;
    canvas.height = 675;

    // Load and draw background image
    const bgImage = new Image();
    bgImage.crossOrigin = "anonymous";
    bgImage.src = '/image/GOTY_bg.jpg';
    await new Promise(resolve => bgImage.onload = resolve);
    ctx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);

    // Add dark overlay for better text visibility
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const padding = 50;
    const neonYellow = 'rgba(189, 189, 189, 1)';

    // Calculate grid position first (needed for alignment)
    const cols = 3;
    const coverSize = 100;
    const gapX = 14;
    const gapY = 14;
    const categoryLabelHeight = 14;
    const spaceBetweenLabelAndCover = 6;

    const totalGridWidth = cols * coverSize + (cols - 1) * gapX;
    const cellHeight = categoryLabelHeight + spaceBetweenLabelAndCover + coverSize * (4/3);
    const totalGridHeight = 3 * cellHeight + 2 * gapY;
    const gridStartX = (canvas.width - totalGridWidth) / 2;
    const gridStartY = canvas.height - totalGridHeight - padding;

    // Draw main game (overall winner) - aligned with grid top
    const overallSelection = data.selections.find(s => s.category === GotyCategory.OVERALL);

    if (overallSelection?.game && data.overall_game) {
      const mainCover = new Image();
      mainCover.crossOrigin = "anonymous";
      mainCover.src = data.overall_game.cover_hd || data.overall_game.cover || '/image/no_image.png';
      await new Promise(resolve => mainCover.onload = resolve);

      const mainWidth = 320;
      const mainHeight = mainWidth * (4/3);
      const mainX = padding;
      const mainY = gridStartY + categoryLabelHeight + spaceBetweenLabelAndCover; // Align with grid covers

      // Draw "WINNER" or "MyGOTY" label (neon yellow, bold, above the image, aligned with category labels)
      ctx.fillStyle = neonYellow;
      ctx.font = '900 32px Arial, sans-serif';
      ctx.textAlign = 'left';
      const winnerLabel = isMyGoty ? '★MyGOTY' : '★WINNER';
      ctx.fillText(winnerLabel, mainX, gridStartY + categoryLabelHeight - 10);

      // Draw semi-transparent border for main game
      ctx.strokeStyle = 'rgba(189, 189, 189, 0.25)';
      ctx.lineWidth = 15;
      ctx.strokeRect(mainX, mainY, mainWidth, mainHeight);

      // Draw main game cover (sharp edges)
      ctx.drawImage(mainCover, mainX, mainY, mainWidth, mainHeight);

      // Draw game title below cover (white, bold)
      ctx.fillStyle = '#ffffffc5';
      ctx.font = '700 18px Arial, "Hiragino Kaku Gothic ProN", sans-serif';
      ctx.textAlign = 'left';
      const gameName = overallSelection.game.japanese_name || overallSelection.game.name;
      const maxWidth = mainWidth;
      let gameTitle = gameName;
      if (ctx.measureText(gameTitle).width > maxWidth) {
        while (ctx.measureText(gameTitle + '...').width > maxWidth && gameTitle.length > 0) {
          gameTitle = gameTitle.slice(0, -1);
        }
        gameTitle += '...';
      }
      ctx.fillText(gameTitle, mainX, mainY + mainHeight + 32);
    }

    // Draw bold year number and title (huge, top RIGHT)
    const textStartX = canvas.width - padding;

    // Draw bold year number (huge, neon yellow, top right)
    ctx.fillStyle = neonYellow;
    ctx.font = '900 120px Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(year.toString(), textStartX, padding + 95);

    // Draw "GAME OF THE YEAR" title (stacked, bold, white, tight spacing, right-aligned)
    ctx.fillStyle = '#ffffff';
    ctx.font = '900 46px Arial, sans-serif';
    ctx.textAlign = 'right';
    const titleLines = isMyGoty ? ['MY GAME', 'OF THE', 'YEAR'] : ['GAME OF', 'THE YEAR'];
    let titleY = padding + 155;
    titleLines.forEach(line => {
      ctx.fillText(line, textStartX, titleY);
      titleY += 52;
    });

    // Draw period information for GOTY (not MyGOTY) - below GOTY title (close grouping)
    if (!isMyGoty) {
      ctx.fillStyle = 'rgba(255, 255, 255, 1)';
      ctx.font = '400 20px Arial, sans-serif';
      ctx.textAlign = 'right';
      const prevYear = year - 1;
      const startDate = `${prevYear}/12/01`;
      const endDate = `${year}/11/30`;
      ctx.fillText('Period:', textStartX, titleY + 8);
      ctx.fillText(`${startDate} - ${endDate}`, textStartX, titleY + 38);
    }

    // Draw user info with neon yellow accent (right-aligned, separated from period)
    const userInfoY = isMyGoty ? titleY + 30 : titleY + 100; // More spacing to separate from GOTY group
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 22px Arial, "Hiragino Kaku Gothic ProN", sans-serif';
    ctx.textAlign = 'right';
    const displayNameUpper = data.user.display_name.toUpperCase();
    ctx.fillText(displayNameUpper, textStartX, userInfoY);

    ctx.fillStyle = '#d4d4d4ff';
    ctx.font = '700 22px Arial, sans-serif';
    const nameWidth = ctx.measureText(displayNameUpper).width;
    ctx.fillText('by', textStartX - nameWidth - 10, userInfoY);

    // Draw logo (bottom right, small)
    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.src = '/pulse_logo.png';
    await new Promise(resolve => logo.onload = resolve);
    const logoHeight = 40;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    const logoX = canvas.width - logoWidth - padding;
    const logoY = canvas.height - logoHeight - padding;
    ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);

    // Draw URL below logo
    ctx.fillStyle = 'rgba(199, 199, 199, 1)';
    ctx.font = '400 16px Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('https://plsdb.com', canvas.width - padding, logoY - 16);

    // Draw other category games in a 3x3 grid at the bottom center
    const otherCategories = [
      GotyCategory.VISUAL,
      GotyCategory.SOUND,
      GotyCategory.GAMEPLAY,
      GotyCategory.NARRATIVE,
      GotyCategory.ORIGINALITY,
      GotyCategory.MULTIPLAYER,
      GotyCategory.INDIE,
      GotyCategory.REMAKE,
      GotyCategory.ACCESSIBILITY
    ];

    for (let i = 0; i < otherCategories.length; i++) {
      const category = otherCategories[i];
      const selection = data.selections.find(s => s.category === category);

      const col = i % cols;
      const row = Math.floor(i / cols);
      const cellX = gridStartX + col * (coverSize + gapX);
      const cellY = gridStartY + row * (cellHeight + gapY);

      // Draw category label (small, neon yellow, consistent position, English only)
      ctx.fillStyle = neonYellow;
      ctx.font = '600 12px Arial, sans-serif';
      ctx.textAlign = 'left';
      const categoryLabel = GotyCategoryLabels[category].en.toUpperCase().replace('BEST ', '').replace('/REMASTER', '');
      ctx.fillText(categoryLabel, cellX, cellY + categoryLabelHeight);

      const coverX = cellX;
      const coverY = cellY + categoryLabelHeight + spaceBetweenLabelAndCover;

      if (selection?.game) {
        const cover = new Image();
        cover.crossOrigin = "anonymous";
        cover.src = selection.game.cover || '/image/no_image.png';
        await new Promise(resolve => cover.onload = resolve);

        // Draw cover with semi-transparent border (sharp edges)
        ctx.strokeStyle = 'rgba(189, 189, 189, 0.3)';
        ctx.lineWidth = 6;
        ctx.strokeRect(coverX, coverY, coverSize, coverSize * (4/3));
        ctx.drawImage(cover, coverX, coverY, coverSize, coverSize * (4/3));
      } else {
        // Draw placeholder for empty selection (neon yellow border)
        ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.strokeStyle = 'rgba(230, 230, 230, 0.2)';
        ctx.lineWidth = 3;
        ctx.strokeRect(coverX, coverY, coverSize, coverSize * (4/3));
        ctx.fillRect(coverX, coverY, coverSize, coverSize * (4/3));
      }
    }

    // Reset text alignment
    ctx.textAlign = 'left';
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Check if mobile device
    if (/Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      const image = canvas.toDataURL('image/png');
      const byteString = atob(image.split(',')[1]);
      const mimeString = image.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } else {
      const link = document.createElement('a');
      link.download = `${isMyGoty ? 'MyGOTY' : 'GOTY'}-${year}-${data.user.username}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  useEffect(() => {
    generateThumbnail();
  }, [data, year, isMyGoty]);

  return (
    <Box sx={{
      mt: 3,
      maxWidth: '800px',
      mx: 'auto'
    }}>
      <Box sx={{
        border: '1px solid',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'rgba(0, 0, 0, 0.2)'
      }}>
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block'
          }}
        />
        <Box sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5
        }}>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '0.75rem',
              lineHeight: 1.4
            }}
          >
            サムネイルはダウンロードしてGOTYのURLと併せて利用してください。
          </Typography>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{
              fontSize: '0.8rem',
              py: 1,
              textTransform: 'none',
              borderColor: 'rgba(102, 126, 234, 0.5)',
              color: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                borderColor: 'rgba(102, 126, 234, 0.8)',
                backgroundColor: 'rgba(102, 126, 234, 0.1)'
              }
            }}
          >
            ダウンロード
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default GotyThumbnailGenerator;
